import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';
import '../../../../styles/tables.css';

const MySubscription = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await distributorService.getMySubscription();
      if (result.success && result.data) {
        setSubscription(result.data);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Started';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      active: { bg: 'success', label: 'Active', icon: 'bi-check-circle-fill', color: '#10B981' },
      expired: { bg: 'danger', label: 'Expired', icon: 'bi-x-circle-fill', color: '#EF4444' },
      cancelled: { bg: 'secondary', label: 'Cancelled', icon: 'bi-slash-circle', color: '#6B7280' },
      pending: { bg: 'warning', label: 'Pending Payment', icon: 'bi-clock-fill', color: '#F59E0B' }
    };
    return configs[status?.toLowerCase()] || { bg: 'secondary', label: status || 'Unknown', icon: 'bi-question-circle', color: '#6B7280' };
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" style={{ color: '#7C3AED' }} />
          <p className="mt-3 text-muted">Loading subscription details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger" className="text-center">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <Button variant="outline-danger" size="sm" className="ms-3" onClick={fetchSubscription}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!subscription) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div
            className="mx-auto mb-4 d-flex align-items-center justify-content-center"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)'
            }}
          >
            <i className="bi bi-credit-card-2-front" style={{ fontSize: '3rem', color: '#7C3AED' }}></i>
          </div>
          <h3 className="fw-bold mb-2" style={{ color: '#1F2937' }}>No Active Subscription</h3>
          <p className="text-muted mb-4">
            You don't have an active subscription. Subscribe to a plan to access all features.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/distributor/subscription/plans')}
            style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              border: 'none',
              borderRadius: '25px',
              padding: '12px 32px'
            }}
          >
            <i className="bi bi-bag-check me-2"></i>
            View Plans
          </Button>
        </div>
      </Container>
    );
  }

  const plan = subscription.plan || {};
  const features = plan.features || {};
  const statusConfig = getStatusConfig(subscription.status);

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0" style={{ color: '#4B0082' }}>
          My Subscription
        </h2>
        <Button
          variant="outline-primary"
          onClick={fetchSubscription}
          style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </Button>
      </div>

      {/* Pending Payment Alert */}
      {subscription.status === 'pending' && (
        <Alert
          className="mb-4 border-0"
          style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <i className="bi bi-exclamation-triangle-fill me-2 text-warning"></i>
              <strong>Payment Pending!</strong> Complete your payment to activate your subscription.
            </div>
            <Button
              style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                border: 'none',
                borderRadius: '20px'
              }}
            >
              Complete Payment
            </Button>
          </div>
        </Alert>
      )}

      <Row>
        {/* Main Subscription Card */}
        <Col lg={8}>
          <Card
            className="mb-4 border-0"
            style={{
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
              overflow: 'hidden'
            }}
          >
            {/* Card Header with Gradient */}
            <div
              style={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)',
                padding: '24px',
                position: 'relative'
              }}
            >
              <svg
                viewBox="0 0 500 80"
                preserveAspectRatio="none"
                style={{
                  position: 'absolute',
                  bottom: '-1px',
                  left: 0,
                  width: '100%',
                  height: '40px'
                }}
              >
                <path d="M0,80 L0,40 Q250,80 500,40 L500,80 Z" fill="#ffffff" />
              </svg>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <Badge
                    className="mb-2 px-3 py-2"
                    style={{
                      background: statusConfig.color,
                      fontSize: '0.85rem'
                    }}
                  >
                    <i className={`bi ${statusConfig.icon} me-2`}></i>
                    {statusConfig.label}
                  </Badge>
                  <h3 className="text-white fw-bold mb-1">{plan.name || 'N/A'}</h3>
                  <p className="text-white-50 mb-0" style={{ fontSize: '0.9rem' }}>
                    Plan Code: {plan.code || 'N/A'}
                  </p>
                </div>
                <div className="text-end">
                  <h2 className="text-white fw-bold mb-0">{formatCurrency(plan.price)}</h2>
                  <span className="text-white-50">per {plan.billing_cycle || 'month'}</span>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <Card.Body className="p-4">
              {/* Subscription Details Grid */}
              <Row className="mb-4">
                <Col sm={6} md={3} className="mb-3">
                  <div className="text-center p-3" style={{ background: '#F9FAFB', borderRadius: '12px' }}>
                    <i className="bi bi-calendar-check text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                    <small className="d-block text-muted">Start Date</small>
                    <strong style={{ color: '#1F2937' }}>{formatDate(subscription.start_date)}</strong>
                  </div>
                </Col>
                <Col sm={6} md={3} className="mb-3">
                  <div className="text-center p-3" style={{ background: '#F9FAFB', borderRadius: '12px' }}>
                    <i className="bi bi-calendar-x text-danger mb-2" style={{ fontSize: '1.5rem' }}></i>
                    <small className="d-block text-muted">End Date</small>
                    <strong style={{ color: '#1F2937' }}>{formatDate(subscription.end_date)}</strong>
                  </div>
                </Col>
                <Col sm={6} md={3} className="mb-3">
                  <div className="text-center p-3" style={{ background: '#F9FAFB', borderRadius: '12px' }}>
                    <i className="bi bi-clock-history text-warning mb-2" style={{ fontSize: '1.5rem' }}></i>
                    <small className="d-block text-muted">Days Remaining</small>
                    <strong style={{ color: '#1F2937' }}>
                      {subscription.days_remaining !== null ? `${subscription.days_remaining} days` : 'N/A'}
                    </strong>
                  </div>
                </Col>
                <Col sm={6} md={3} className="mb-3">
                  <div className="text-center p-3" style={{ background: '#F9FAFB', borderRadius: '12px' }}>
                    <i className={`bi ${subscription.auto_renew ? 'bi-arrow-repeat text-success' : 'bi-x-circle text-secondary'} mb-2`} style={{ fontSize: '1.5rem' }}></i>
                    <small className="d-block text-muted">Auto Renew</small>
                    <strong style={{ color: '#1F2937' }}>{subscription.auto_renew ? 'Enabled' : 'Disabled'}</strong>
                  </div>
                </Col>
              </Row>

              {/* Plan Duration */}
              <div
                className="p-3 mb-4 d-flex justify-content-between align-items-center"
                style={{ background: '#EDE9FE', borderRadius: '12px' }}
              >
                <div>
                  <i className="bi bi-calendar3 me-2" style={{ color: '#7C3AED' }}></i>
                  <span style={{ color: '#5B21B6' }}>Plan Duration</span>
                </div>
                <strong style={{ color: '#5B21B6' }}>{plan.duration_days || 30} Days</strong>
              </div>

              {/* Features Section */}
              <h6 className="fw-bold mb-3" style={{ color: '#1F2937' }}>
                <i className="bi bi-check2-square me-2" style={{ color: '#7C3AED' }}></i>
                Plan Features
              </h6>
              <Row>
                {[
                  { key: 'dms_access', label: 'Complete DMS Access', icon: 'bi-database' },
                  { key: 'distributor_dashboard', label: 'Distributor Dashboard', icon: 'bi-speedometer2' },
                  { key: 'retailer_payment_system', label: 'Retailer Payment System', icon: 'bi-credit-card' },
                  { key: 'ca_login', label: 'CA Login Access', icon: 'bi-person-badge' },
                  { key: 'compliance_reports', label: 'Compliance Reports', icon: 'bi-file-earmark-text' }
                ].map((feature) => (
                  <Col sm={6} key={feature.key} className="mb-2">
                    <div className="d-flex align-items-center">
                      {features[feature.key] ? (
                        <i className="bi bi-check-circle-fill me-2" style={{ color: '#10B981' }}></i>
                      ) : (
                        <i className="bi bi-x-circle-fill me-2" style={{ color: '#EF4444' }}></i>
                      )}
                      <span style={{ color: features[feature.key] ? '#374151' : '#9CA3AF' }}>
                        {feature.label}
                      </span>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Subscription Info Card */}
          <Card
            className="mb-4 border-0"
            style={{
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
          >
            <Card.Body className="p-4">
              <h6 className="fw-bold mb-3" style={{ color: '#1F2937' }}>
                <i className="bi bi-info-circle me-2" style={{ color: '#7C3AED' }}></i>
                Subscription Info
              </h6>
              <div className="mb-3 pb-3 border-bottom">
                <small className="text-muted d-block">Subscription ID</small>
                <code style={{ color: '#7C3AED' }}>#{subscription.id}</code>
              </div>
              <div className="mb-3 pb-3 border-bottom">
                <small className="text-muted d-block">Created On</small>
                <strong>{formatDate(subscription.created_at)}</strong>
              </div>
              <div>
                <small className="text-muted d-block">Status</small>
                <Badge
                  className="mt-1"
                  style={{
                    background: statusConfig.color,
                    padding: '6px 12px'
                  }}
                >
                  {statusConfig.label}
                </Badge>
              </div>
            </Card.Body>
          </Card>

          {/* Quick Actions Card */}
          <Card
            className="mb-4 border-0"
            style={{
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
          >
            <Card.Body className="p-4">
              <h6 className="fw-bold mb-3" style={{ color: '#1F2937' }}>
                <i className="bi bi-lightning me-2" style={{ color: '#7C3AED' }}></i>
                Quick Actions
              </h6>
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/distributor/subscription/plans')}
                  style={{
                    borderColor: '#7C3AED',
                    color: '#7C3AED',
                    borderRadius: '10px'
                  }}
                >
                  <i className="bi bi-grid me-2"></i>
                  View All Plans
                </Button>
                {subscription.status === 'active' && (
                  <Button
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                      border: 'none',
                      borderRadius: '10px'
                    }}
                    onClick={() => navigate('/distributor/subscription/plans')}
                  >
                    <i className="bi bi-arrow-up-circle me-2"></i>
                    Upgrade Plan
                  </Button>
                )}
                {(subscription.status === 'expired' || subscription.status === 'cancelled') && (
                  <Button
                    variant="success"
                    style={{ borderRadius: '10px' }}
                    onClick={() => navigate('/distributor/subscription/plans')}
                  >
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Renew Subscription
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Help Card */}
          <Card
            className="border-0 text-center"
            style={{
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)'
            }}
          >
            <Card.Body className="p-4">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#fff'
                }}
              >
                <i className="bi bi-headset" style={{ fontSize: '1.5rem', color: '#7C3AED' }}></i>
              </div>
              <h6 className="fw-bold" style={{ color: '#1F2937' }}>Need Help?</h6>
              <p className="text-muted small mb-3">
                Contact our support team for any subscription-related queries.
              </p>
              <Button
                variant="outline-secondary"
                size="sm"
                style={{ borderRadius: '20px' }}
              >
                <i className="bi bi-chat-dots me-2"></i>
                Contact Support
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MySubscription;
